import {useEffect, useEffectEvent, useRef, type RefObject} from 'react'
import {hasWebGLSupport} from '../../../lib/utils/platform'
import type {
  MonaCanvasBounds,
  MonaCanvasFaceDirection,
  MonaCanvasFraming,
  MonaCanvasMouseInteractionType,
  MonaCanvasOpeningAnimation,
} from './types'

import styles from './MonaCanvas.module.css'
import type {MonaColorVariant} from './config'
import Artwork from './webgl/artwork'

// Keep the mouse position and Mona's movement current offscreen so returning with the mouse far
// from its last position does not make Mona move too quickly to catch up. Update every N animation
// frames to balance offscreen work with movement continuity; this reduces update frequency, not speed.
const OUT_OF_VIEW_UPDATE_FRAME_INTERVAL = 20

export interface MonaCanvasProps {
  framing?: MonaCanvasFraming
  colorVariant?: MonaColorVariant
  createOpeningAnimation?: () => MonaCanvasOpeningAnimation
  openingAnimationKey?: string | number
  skipOpeningAnimation?: boolean
  modelScale?: number
  mouseInteractionType?: MonaCanvasMouseInteractionType
  mouseInteractionRadius?: number
  mouseInteractionRestingPitch?: number
  faceDirectionRef?: RefObject<MonaCanvasFaceDirection | null>
  onBeforeRender?: (timestamp: number) => void
  onOpeningStart?: () => void
  onOpeningComplete?: () => void
  onFallback: () => void
  onMonaCanvasBoundsChange?: (bounds: MonaCanvasBounds | null) => void
}

export function MonaCanvas({
  framing = 'scene',
  colorVariant = 'black',
  createOpeningAnimation,
  openingAnimationKey,
  skipOpeningAnimation = false,
  modelScale = 1,
  mouseInteractionType = 'none',
  mouseInteractionRadius,
  mouseInteractionRestingPitch = 0,
  faceDirectionRef,
  onBeforeRender,
  onOpeningStart,
  onOpeningComplete,
  onFallback,
  onMonaCanvasBoundsChange,
}: MonaCanvasProps) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationFrameRef = useRef<number | null>(null)
  const artworkRef = useRef<Artwork | null>(null)
  // Read the current setting without restarting the canvas or its opening animation.
  const syncMouseInteraction = useEffectEvent(() => {
    artworkRef.current?.setMouseInteraction(mouseInteractionType, mouseInteractionRadius, mouseInteractionRestingPitch)
    return mouseInteractionType
  })
  const syncFaceDirection = useEffectEvent(() => artworkRef.current?.setFaceDirection(faceDirectionRef?.current))
  const notifyOpeningStart = useEffectEvent(() => onOpeningStart?.())
  const notifyOpeningComplete = useEffectEvent(() => onOpeningComplete?.())
  const updateBeforeRender = useEffectEvent((timestamp: number) => onBeforeRender?.(timestamp))
  const getOpeningAnimationKey = useEffectEvent(() => openingAnimationKey)
  const shouldSkipOpeningAnimation = useEffectEvent(() => skipOpeningAnimation)
  const syncModelScale = useEffectEvent(() => artworkRef.current?.setModelScale(modelScale))

  useEffect(() => {
    if (!hasWebGLSupport()) {
      const fallbackTimeout = window.setTimeout(onFallback, 0)

      return () => window.clearTimeout(fallbackTimeout)
    }

    const wrapper = wrapperRef.current
    const canvas = canvasRef.current
    if (!wrapper || !canvas) return

    let fallbackTimeout: number | undefined
    let isInViewport = false
    let hasEnteredViewport = false
    let isVisible = false
    let hasFailed = false
    let pendingResize: Pick<DOMRect, 'height' | 'width'> | null = null
    let appliedHeight = 0
    let appliedWidth = 0
    let renderingCount = 0
    let pendingMouseMove: MouseEvent | null = null
    let appliedOpeningAnimationKey = getOpeningAnimationKey()

    const stopRendering = () => {
      if (animationFrameRef.current === null) return

      window.cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }

    const handleArtworkError = (error: unknown) => {
      if (hasFailed) return

      hasFailed = true
      window.reportError(error)
      stopRendering()
      onMonaCanvasBoundsChange?.(null)
      fallbackTimeout = window.setTimeout(onFallback, 0)
    }

    const handleResize = ({height, width}: Pick<DOMRect, 'height' | 'width'>) => {
      pendingResize = {height, width}
      // Cancel the pending frame before restarting; the next frame applies the latest size.
      stopRendering()
      startRendering()
    }

    const applyPendingResize = () => {
      if (!pendingResize || hasFailed || !hasEnteredViewport) return

      const {height, width} = pendingResize
      pendingResize = null
      isVisible = width > 0 && height > 0

      if (!isVisible || (width === appliedWidth && height === appliedHeight)) return

      if (!artworkRef.current) {
        try {
          artworkRef.current = new Artwork({
            canvas,
            colorVariant,
            createOpeningAnimation,
            framing,
            onError: handleArtworkError,
            onOpeningStart: notifyOpeningStart,
            onOpeningComplete: notifyOpeningComplete,
            pixelRatio: Math.min(window.devicePixelRatio, 1.75),
            reduceMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
            wrapper,
          })
          appliedOpeningAnimationKey = getOpeningAnimationKey()
        } catch (error) {
          handleArtworkError(error)
          return
        }
      }

      artworkRef.current.resize(width, height)
      appliedHeight = height
      appliedWidth = width
    }

    const handleAnimationFrame = (timestamp: number) => {
      animationFrameRef.current = null
      applyPendingResize()
      if (hasFailed) return
      try {
        syncModelScale()
        const nextOpeningAnimationKey = getOpeningAnimationKey()
        if (artworkRef.current && appliedOpeningAnimationKey !== nextOpeningAnimationKey) {
          artworkRef.current.restartOpening()
          appliedOpeningAnimationKey = nextOpeningAnimationKey
          pendingMouseMove = null
        }
        if (shouldSkipOpeningAnimation()) artworkRef.current?.skipOpening()
      } catch (error) {
        handleArtworkError(error)
        return
      }

      if (isVisible && !isInViewport) {
        renderingCount += 1
      } else {
        renderingCount = 0
      }

      if (renderingCount % OUT_OF_VIEW_UPDATE_FRAME_INTERVAL === 0) {
        renderingCount = 0
        if (isVisible) updateBeforeRender(timestamp)
        const interactionType = syncMouseInteraction()
        if (hasFailed) return
        if (interactionType === 'none') pendingMouseMove = null
        syncFaceDirection()
        if (isVisible && pendingMouseMove) {
          artworkRef.current?.handleMouseMove(pendingMouseMove)
          pendingMouseMove = null
        }

        const monaCanvasBounds = artworkRef.current?.update(isInViewport, isVisible) ?? null
        onMonaCanvasBoundsChange?.(monaCanvasBounds)
      }

      if (isVisible) startRendering()
    }

    const startRendering = () => {
      if (hasFailed || animationFrameRef.current !== null) return

      animationFrameRef.current = window.requestAnimationFrame(handleAnimationFrame)
    }

    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        isInViewport = entry.isIntersecting
        if (isInViewport) hasEnteredViewport = true
      }
      if (!isInViewport) onMonaCanvasBoundsChange?.(null)
      startRendering()
    })
    observer.observe(wrapper)

    const resizeObserver = new ResizeObserver(([entry]) => {
      if (entry) handleResize(entry.contentRect)
    })
    resizeObserver.observe(wrapper)

    const handleMouseMove = (event: MouseEvent) => {
      if (hasFailed || !isVisible) return
      const interactionType = syncMouseInteraction()
      if (hasFailed || interactionType === 'none') return
      if (!isInViewport) {
        pendingMouseMove = event
        return
      }

      pendingMouseMove = null
      artworkRef.current?.handleMouseMove(event)
    }
    const resetMouseFacing = () => {
      if (hasFailed) return
      const interactionType = syncMouseInteraction()
      if (hasFailed || interactionType !== 'face') return
      pendingMouseMove = null
      artworkRef.current?.resetMouseFacing()
    }
    const handleMouseOut = (event: MouseEvent) => {
      if (event.relatedTarget === null) {
        resetMouseFacing()
      }
    }
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseout', handleMouseOut)
    window.addEventListener('blur', resetMouseFacing)

    handleResize(wrapper.getBoundingClientRect())

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseout', handleMouseOut)
      window.removeEventListener('blur', resetMouseFacing)
      if (fallbackTimeout !== undefined) window.clearTimeout(fallbackTimeout)
      observer.disconnect()
      resizeObserver.disconnect()
      stopRendering()
      artworkRef.current?.dispose()
      artworkRef.current = null
    }
  }, [colorVariant, createOpeningAnimation, framing, onFallback, onMonaCanvasBoundsChange])

  return (
    <div aria-hidden="true" className={styles.Wrapper} ref={wrapperRef}>
      <canvas className={styles.Canvas} ref={canvasRef} />
    </div>
  )
}
